"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import type { Dictionary } from "@/dictionaries/types";
import { useCollectionAnalytics } from "@/features/collections/hooks/useCollectionAnalytics";
import { StatePanel } from "@/features/home-v2/ui";
import AnalyticsChart from "../AnalyticsChart";
import { RetryButton } from "../parts";
type Props = {
  contract: string;
  lang: "fa" | "en";
  currency: string;
  t: Dictionary["collectionPage"];
  states: Dictionary["states"];
  emptyAction: ReactNode;
};

/**
 * THE MARKET READING — the same hook and the same honesty rules as the first
 * design (a failed fetch and a never-traded collection kept apart, a missing
 * metric named, never zeroed). Three headline figures, the four that bound
 * them, the chart, and each marketplace as its share of the volume.
 */
export default function VitrineAnalytics(t0) {
  const $ = _c(110);
  const {
    contract,
    lang,
    currency,
    t,
    states,
    emptyAction
  } = t0;
  const locale = lang === "fa" ? "fa-IR" : "en-US";
  const {
    analytics,
    isLoading,
    isSuccess,
    reset
  } = useCollectionAnalytics(contract, lang);
  if (isLoading) {
    let t1;
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="vt-kpis">{Array.from({
          length: 3
        }, _temp)}</div>;
      t2 = <span className="v2-cd-shimmer v2-cd-skel-chart" />;
      $[0] = t1;
      $[1] = t2;
    } else {
      t1 = $[0];
      t2 = $[1];
    }
    let t3;
    if ($[2] !== states.loading || $[3] !== t1) {
      t3 = <div className="vt-stack" role="status" aria-label={states.loading}>{t1}{t2}</div>;
      $[2] = states.loading;
      $[3] = t1;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (!analytics) {
    let t1;
    if ($[5] !== emptyAction || $[6] !== isSuccess || $[7] !== reset || $[8] !== states || $[9] !== t) {
      t1 = isSuccess ? <StatePanel title={t.analyticsEmptyTitle} body={t.analyticsEmptyBody} action={emptyAction} /> : <StatePanel alert={true} title={states.errorTitle} body={states.errorBody} action={<RetryButton label={states.retry} onRetry={() => void reset()} />} />;
      $[5] = emptyAction;
      $[6] = isSuccess;
      $[7] = reset;
      $[8] = states;
      $[9] = t;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    return t1;
  }
  const {
    stats,
    charts
  } = analytics;
  let t1;
  if ($[11] !== analytics.marketplaces) {
    t1 = analytics.marketplaces ?? [];
    $[11] = analytics.marketplaces;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const markets = t1;
  const finite = _temp2;
  let t2;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden={true}>—</span>;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== t.metricUnavailable) {
    t3 = <span className="v2-sr-only">{t.metricUnavailable}</span>;
    $[14] = t.metricUnavailable;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== t.metricUnavailable || $[17] !== t3) {
    t4 = <span className="vt-na v2-tip" data-tip={t.metricUnavailable} tabIndex={0}>{t2}{t3}</span>;
    $[16] = t.metricUnavailable;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const na = t4;
  let t5;
  if ($[19] !== currency || $[20] !== locale || $[21] !== na) {
    t5 = n_0 => finite(n_0) ? <>{Number(n_0).toLocaleString(locale, {
        maximumFractionDigits: 4
      })}<small>{currency}</small></> : na;
    $[19] = currency;
    $[20] = locale;
    $[21] = na;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const amount = t5;
  let t6;
  if ($[23] !== locale || $[24] !== na) {
    t6 = n_1 => finite(n_1) ? Number(n_1).toLocaleString(locale, {
      maximumFractionDigits: 0
    }) : na;
    $[23] = locale;
    $[24] = na;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const count = t6;
  const hasPoints = (charts?.volume?.length ?? 0) > 0 || (charts?.sales?.length ?? 0) > 0 || (charts?.average_price?.length ?? 0) > 0;
  if (!hasPoints && markets.length === 0 && !(Number(stats?.sales) > 0)) {
    let t7;
    if ($[26] !== emptyAction || $[27] !== t.analyticsEmptyBody || $[28] !== t.analyticsEmptyTitle) {
      t7 = <StatePanel title={t.analyticsEmptyTitle} body={t.analyticsEmptyBody} action={emptyAction} />;
      $[26] = emptyAction;
      $[27] = t.analyticsEmptyBody;
      $[28] = t.analyticsEmptyTitle;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    return t7;
  }
  const t7 = t.volume;
  const t8 = stats?.volume;
  let t9;
  if ($[30] !== amount || $[31] !== t8) {
    t9 = amount(t8);
    $[30] = amount;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t.volume || $[34] !== t9) {
    t10 = {
      label: t7,
      value: t9
    };
    $[33] = t.volume;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const t11 = t.sales;
  const t12 = stats?.sales;
  let t13;
  if ($[36] !== count || $[37] !== t12) {
    t13 = count(t12);
    $[36] = count;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t.sales || $[40] !== t13) {
    t14 = {
      label: t11,
      value: t13
    };
    $[39] = t.sales;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const t15 = t.averagePrice;
  const t16 = stats?.average_price;
  let t17;
  if ($[42] !== amount || $[43] !== t16) {
    t17 = amount(t16);
    $[42] = amount;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t.averagePrice || $[46] !== t17) {
    t18 = {
      label: t15,
      value: t17
    };
    $[45] = t.averagePrice;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t10 || $[49] !== t14 || $[50] !== t18) {
    t19 = [t10, t14, t18];
    $[48] = t10;
    $[49] = t14;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const lead = t19;
  const t20 = t.highestSale;
  const t21 = stats?.highest_sale;
  let t22;
  if ($[52] !== amount || $[53] !== t21) {
    t22 = amount(t21);
    $[52] = amount;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t.highestSale || $[56] !== t22) {
    t23 = {
      label: t20,
      value: t22
    };
    $[55] = t.highestSale;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const t24 = t.lowestSale;
  const t25 = stats?.lowest_sale;
  let t26;
  if ($[58] !== amount || $[59] !== t25) {
    t26 = amount(t25);
    $[58] = amount;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t.lowestSale || $[62] !== t26) {
    t27 = {
      label: t24,
      value: t26
    };
    $[61] = t.lowestSale;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  const t28 = t.uniqueBuyers;
  const t29 = stats?.unique_buyers;
  let t30;
  if ($[64] !== count || $[65] !== t29) {
    t30 = count(t29);
    $[64] = count;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t.uniqueBuyers || $[68] !== t30) {
    t31 = {
      label: t28,
      value: t30
    };
    $[67] = t.uniqueBuyers;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  const t32 = t.uniqueSellers;
  const t33 = stats?.unique_sellers;
  let t34;
  if ($[70] !== count || $[71] !== t33) {
    t34 = count(t33);
    $[70] = count;
    $[71] = t33;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== t.uniqueSellers || $[74] !== t34) {
    t35 = {
      label: t32,
      value: t34
    };
    $[73] = t.uniqueSellers;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t23 || $[77] !== t27 || $[78] !== t31 || $[79] !== t35) {
    t36 = [t23, t27, t31, t35];
    $[76] = t23;
    $[77] = t27;
    $[78] = t31;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  const bounds = t36;
  let t37;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = (sum, mp) => sum + (finite(mp.volume) ? Number(mp.volume) : 0);
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  const totalVolume = markets.reduce(t37, 0);
  let t38;
  if ($[82] !== t.analyticsStatsHeading) {
    t38 = <h3 id="vt-kpi-h" className="v2-sr-only">{t.analyticsStatsHeading}</h3>;
    $[82] = t.analyticsStatsHeading;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== lead) {
    t39 = <dl className="vt-kpis">{lead.map(_temp3)}</dl>;
    $[84] = lead;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] !== bounds) {
    t40 = <dl className="vt-bounds">{bounds.map(_temp4)}</dl>;
    $[86] = bounds;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] !== t38 || $[89] !== t39 || $[90] !== t40) {
    t41 = <section aria-labelledby="vt-kpi-h" className="vt-reading">{t38}{t39}{t40}</section>;
    $[88] = t38;
    $[89] = t39;
    $[90] = t40;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  let t42;
  if ($[92] !== charts || $[93] !== currency || $[94] !== hasPoints || $[95] !== lang || $[96] !== t) {
    t42 = hasPoints ? <AnalyticsChart lang={lang} charts={charts} currency={currency} t={t} /> : null;
    $[92] = charts;
    $[93] = currency;
    $[94] = hasPoints;
    $[95] = lang;
    $[96] = t;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== amount || $[99] !== count || $[100] !== markets || $[101] !== t.marketplacesHeading || $[102] !== t.marketplacesTableLabel || $[103] !== t.sales || $[104] !== totalVolume) {
    t43 = markets.length > 0 ? <section aria-labelledby="vt-mk-h" className="vt-markets"><h3 id="vt-mk-h" className="vt-h3">{t.marketplacesHeading}</h3><ul aria-label={t.marketplacesTableLabel}>{markets.map(mp_0 => {
          const share = totalVolume > 0 && finite(mp_0.volume) ? Number(mp_0.volume) / totalVolume * 100 : 0;
          return <li key={mp_0.name} className="vt-market-row"><span className="vt-market">{mp_0.name}</span><span className="vt-bar" aria-hidden={true}><i style={{
                inlineSize: `${Math.max(share, share > 0 ? 3 : 0)}%`
              }} /></span><span className="vt-market-vol">{amount(mp_0.volume)}</span><span className="vt-market-sales">{count(mp_0.sales)} <small>{t.sales}</small></span></li>;
        })}</ul></section> : null;
    $[98] = amount;
    $[99] = count;
    $[100] = markets;
    $[101] = t.marketplacesHeading;
    $[102] = t.marketplacesTableLabel;
    $[103] = t.sales;
    $[104] = totalVolume;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] !== t41 || $[107] !== t42 || $[108] !== t43) {
    t44 = <div className="vt-stack">{t41}{t42}{t43}</div>;
    $[106] = t41;
    $[107] = t42;
    $[108] = t43;
    $[109] = t44;
  } else {
    t44 = $[109];
  }
  return t44;
}
function _temp4(k_0) {
  return <div key={k_0.label}><dt>{k_0.label}</dt><dd>{k_0.value}</dd></div>;
}
function _temp3(k) {
  return <div key={k.label} className="vt-kpi"><dt>{k.label}</dt><dd>{k.value}</dd></div>;
}
function _temp2(n) {
  return n != null && Number.isFinite(Number(n));
}
function _temp(_, i) {
  return <span key={i} className="v2-cd-shimmer vt-skel-kpi" />;
}